import http from '../shared/HttpCommon';
import { Dispatch, SetStateAction } from 'react';
import { ISurvey } from '../shared/interfaces/ISurvey';
import { CanceledError } from 'axios';
import ITrendResponse from '../shared/interfaces/ITrendResponse';
import { ISurveyStandardEntry } from '../shared/interfaces/ISurveyStandardEntry';
import { ICustomSurveyEntry } from '../shared/interfaces/ICustomSurveyEntry';
import { ICustomSurveyQuestion } from '../shared/interfaces/ICustomSurveyQuestion';

const getSurvey = async (loading: Dispatch<SetStateAction<boolean>>, signal: AbortSignal) => {
  try {
    loading(true);
    const { data } = await http.get('/surveys', { signal });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getQuestions = async (
  surveyId: string,
  type: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/survey/${type.toLowerCase()}/${surveyId}/questions`, {
      signal,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getSurveyPublic = async (
  surveyId: string,
  type: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/survey/public/${type}/${surveyId}`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getNpsSurveyScore = async (
  surveyId: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/survey/nps/${surveyId}/score`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const addNpsSurvey = async (NPSSurvey: ISurvey) => {
  try {
    const response = await http.post('/survey/nps', NPSSurvey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const archivedSurvey = async (surveyId: string, archived: boolean) => {
  try {
    const { data } = await http.patch(`/survey/${surveyId}/status`, { archived });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveSurveyNote = async (surveyId: string, note: string) => {
  try {
    const { data } = await http.patch(`/survey/${surveyId}/note`, { note });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveNpsSurveyAnswer = async (
  surveyId: string,
  answer: number,
  email: string,
  feedback: string,
  name: string
) => {
  try {
    const { data } = await http.post(`/survey/public/nps/${surveyId}/answer`, {
      answer,
      email,
      feedback,
      name,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateNpsSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: number,
  email: string,
  feedback: string,
  name: string
) => {
  try {
    const { data } = await http.patch(`/survey/public/nps/${surveyId}/answer/${answerId}`, {
      answer,
      email,
      feedback,
      name,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveTrendSurveyAnswer = async (surveyId: string, answer: string, feedback: string) => {
  try {
    const { data } = await http.post(`/survey/public/trend/${surveyId}/answer`, {
      answer,
      feedback,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateTrendSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: string,
  feedback: string
) => {
  try {
    const { data } = await http.patch(`/survey/public/trend/${surveyId}/answer/${answerId}`, {
      answer,
      feedback,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateTrendSurvey = async (surveyId: string, survey: ISurvey) => {
  try {
    const response = await http.put(`/survey/trend/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateNpsSurvey = async (surveyId: string, survey: ISurvey) => {
  try {
    const response = await http.put(`/survey/nps/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateSurvey = async (surveyId: string, survey: ISurveyStandardEntry) => {
  try {
    const response = await http.put(`/survey/standard/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addQuestion = async (surveyId: string, type: string, survey: ICustomSurveyQuestion) => {
  try {
    const response = await http.post(`/survey/${type.toLowerCase()}/${surveyId}/question`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateCustomsurvey = async (surveyId: string, survey: ICustomSurveyEntry) => {
  try {
    const response = await http.put(`/survey/customsurvey/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveStandardSurveyAnswer = async (
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string }[]
) => {
  try {
    const { data } = await http.post(`/survey/public/standard/${surveyId}/answer`, {
      answers: answer,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveCustomSurveyAnswer = async (
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string }[]
) => {
  try {
    const { data } = await http.post(`/survey/public/customsurvey/${surveyId}/answer`, {
      answers: answer,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateStandardSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string }
) => {
  try {
    const { data } = await http.patch(
      `/survey/public/standard/${surveyId}/answer/${answerId}`,
      answer
    );
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const getSurveyResponse = async (surveyId: string, signal: AbortSignal) => {
  try {
    const { data } = await http.get(`/survey/${surveyId}/answers`, { signal });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const getTrendResponse = async (surveyId: string, signal: AbortSignal) => {
  try {
    const { data } = await http.get(`/survey/trend/${surveyId}/answers`, { signal });

    return data.surveyData.map((item: ITrendResponse) => {
      return {
        answers: item.answers,
        averageRating: item.averageRating,
        createdAt: item.createdAt,
        id: item.id,
        surveyId: item.surveyId,
        mood: item.mood,
        score: item.score,
        totalCount: item.totalCount,
      };
    });
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addTrendSurvey = async (trendSurvey: ISurvey) => {
  try {
    const response = await http.post('/survey/trend', trendSurvey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addSurvey = async (survey: ISurveyStandardEntry) => {
  try {
    const response = await http.post('/survey/standard', survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addCustomsurvey = async (survey: ICustomSurveyEntry) => {
  try {
    const response = await http.post('/survey/customsurvey', survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const deleteSurvey = async (surveyId: string) => {
  try {
    const response = await http.delete(`/survey/${surveyId}`);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const deleteQuestion = async (type: string, surveyId: string, questionId: string) => {
  try {
    const response = await http.delete(
      `/survey/${type.toLowerCase()}/${surveyId}/question/${questionId}`
    );
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateQuestion = async (
  type: string,
  surveyId: string,
  questionId: string,
  question: ICustomSurveyQuestion
) => {
  try {
    const response = await http.patch(
      `/survey/${type.toLowerCase()}/${surveyId}/question/${questionId}`,
      question
    );
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const resetSurvey = async (surveyId: string) => {
  try {
    const response = await http.patch(`/survey/${surveyId}/reset`);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const SurveyService = {
  getSurvey,
  archivedSurvey,
  saveSurveyNote,
  saveNpsSurveyAnswer,
  updateNpsSurveyAnswer,
  updateNpsSurvey,
  addNpsSurvey,
  saveTrendSurveyAnswer,
  updateTrendSurveyAnswer,
  getSurveyResponse,
  getSurveyPublic,
  saveStandardSurveyAnswer,
  updateStandardSurveyAnswer,
  addSurvey,
  updateSurvey,
  addCustomsurvey,
  updateCustomsurvey,
  getTrendResponse,
  addTrendSurvey,
  updateTrendSurvey,
  deleteSurvey,
  resetSurvey,
  getNpsSurveyScore,
  addQuestion,
  getQuestions,
  deleteQuestion,
  updateQuestion,
  saveCustomSurveyAnswer,
};

export default SurveyService;
